import React, { useState, useEffect, useCallback } from 'react';
import { 
  Zap, Users, Clock, Shield, Sparkles, ArrowRight, TrendingUp, 
  Rocket, Check, Mail, Bell, Target, Repeat
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CryptoAffiliateBlock = ({ 
  profit = null, 
  cryptoName = 'crypto',
  toolName = 'Crypto Calculator',
  toolSlug = 'crypto-calculator',
  investmentAmount = null
}) => {
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  // Determine if profit is positive
  const profitValue = typeof profit === 'number' ? profit : parseFloat(profit) || null;
  const isPositive = profitValue !== null && profitValue > 0;
  const formattedProfit = profitValue !== null ? Math.abs(profitValue).toLocaleString('en-US', { 
    style: 'currency', 
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2 
  }) : null;

  // Calculate 5x projection
  const projectedProfit = profitValue !== null && profitValue > 0 
    ? (profitValue * 5).toLocaleString('en-US', { 
        style: 'currency', 
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0 
      })
    : null;

  // Detect device type
  const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  };

  // Track tool use on component mount (for CTR calculation)
  useEffect(() => {
    const trackToolUse = async () => {
      try {
        await fetch(`${API_URL}/api/affiliate/tool-use`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tool_slug: toolSlug,
            crypto_type: cryptoName.toUpperCase()
          })
        });
      } catch (err) {
        console.debug('Tool use tracking failed:', err);
      }
    };
    trackToolUse();
  }, [toolSlug, cryptoName]);

  // Track affiliate click
  const trackClick = useCallback(async (platform, affiliateUrl) => {
    try {
      fetch(`${API_URL}/api/affiliate/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platform: platform,
          tool_name: toolName,
          tool_slug: toolSlug,
          crypto_type: cryptoName.toUpperCase(),
          device_type: getDeviceType(),
          profit_shown: profitValue
        })
      }).catch(() => {});
      await new Promise(resolve => setTimeout(resolve, 50));
    } catch (err) {
      // Silent fail
    }
    window.open(affiliateUrl, '_blank', 'noopener,noreferrer');
  }, [toolName, toolSlug, cryptoName, profitValue]);

  const handleBinanceClick = (e) => {
    e.preventDefault();
    trackClick('binance', 'https://www.binance.com/register?ref=1229829685');
  };

  const handleMexcClick = (e) => {
    e.preventDefault();
    trackClick('mexc', 'https://promote.mexc.com/r/T7Uz5eolTa');
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      // Track email capture (can be enhanced with backend later)
      console.log('Email captured:', email, { toolSlug, cryptoName, profit: profitValue });
      setEmailSubmitted(true);
    }
  };

  return (
    <div 
      className="mt-6 p-5 bg-gradient-to-br from-gray-800/90 to-gray-900/95 rounded-xl border border-gray-700/50 backdrop-blur-sm relative overflow-hidden" 
      data-testid="crypto-affiliate-block"
    >
      {/* Subtle gradient glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-transparent to-cyan-500/5 pointer-events-none" />
      
      {/* Trust Badge - Top */}
      <div className="relative flex items-center justify-center gap-2 mb-4">
        <Shield size={14} className="text-green-400" />
        <span className="text-xs text-green-400 font-medium">Trusted by millions of crypto traders worldwide</span>
      </div>

      {/* 5x Profit Projection - NEW */}
      {projectedProfit && (
        <div className="relative mb-4 p-3 rounded-lg bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30">
          <div className="flex items-center justify-center gap-2">
            <Repeat size={16} className="text-purple-400" />
            <span className="text-sm text-gray-300">
              Repeat this 5x → potential: <span className="font-bold text-purple-400">{projectedProfit}</span>
            </span>
            <Target size={16} className="text-purple-400" />
          </div>
        </div>
      )}
      
      {/* Main Headline - ENHANCED */}
      <div className="relative text-center mb-4">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Rocket size={20} className="text-green-400" />
          <h3 className="text-xl font-bold text-white">
            Turn This Into REAL Profit
          </h3>
          <Rocket size={20} className="text-green-400 transform -scale-x-100" />
        </div>
        
        {/* Loss Aversion Trigger */}
        <p className="text-sm text-amber-400/90 mb-3">
          Don't lose gains to high fees or bad execution
        </p>
        
        {/* Dynamic Profit Hook */}
        {profitValue !== null && (
          <div className="mt-3 p-3 bg-gray-900/60 rounded-lg border border-gray-700/50">
            <p className={`text-lg font-bold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
              {isPositive ? (
                <>
                  <span className="text-2xl">+{formattedProfit}</span>
                  <span className="block text-sm font-medium mt-1 text-green-300/80">
                    based on this scenario
                  </span>
                </>
              ) : (
                <span className="text-lg">Minimize losses with smart trading</span>
              )}
            </p>
          </div>
        )}
        
        {/* Urgency Line */}
        <div className="flex items-center justify-center gap-2 mt-3">
          <Clock size={14} className="text-amber-400 animate-pulse" />
          <span className="text-sm text-amber-400 font-medium">Prices change fast — act now</span>
        </div>
      </div>
      
      {/* CTA Buttons - ENHANCED */}
      <div className="relative flex flex-col gap-3">
        {/* Binance Button */}
        <button
          onClick={handleBinanceClick}
          className="group relative flex items-center justify-between p-4 bg-gradient-to-r from-yellow-500/15 to-yellow-600/10 hover:from-yellow-500/25 hover:to-yellow-600/20 border border-yellow-500/40 hover:border-yellow-400/60 rounded-xl transition-all duration-300 shadow-lg hover:shadow-yellow-500/20 w-full text-left"
          data-testid="binance-affiliate-link"
        >
          <div className="absolute inset-0 rounded-xl bg-yellow-400/0 group-hover:bg-yellow-400/5 transition-all duration-300" />
          
          <div className="relative flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-yellow-500/20 flex items-center justify-center border border-yellow-500/30 group-hover:scale-110 transition-transform duration-300">
              <span className="text-2xl font-bold text-yellow-400">B</span>
            </div>
            <div className="text-left">
              <p className="font-bold text-white group-hover:text-yellow-300 transition-colors text-base">
                Buy {cryptoName.toUpperCase()} (Beginner Friendly)
              </p>
              {/* Benefit Bullets - NEW */}
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Check size={10} className="text-green-400" />
                  Low fees
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Check size={10} className="text-green-400" />
                  Fast signup
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Check size={10} className="text-green-400" />
                  Trusted platform
                </span>
              </div>
            </div>
          </div>
          <ArrowRight size={22} className="relative text-yellow-400 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
        </button>

        {/* MEXC Button */}
        <button
          onClick={handleMexcClick}
          className="group relative flex items-center justify-between p-4 bg-gradient-to-r from-cyan-500/15 to-cyan-600/10 hover:from-cyan-500/25 hover:to-cyan-600/20 border border-cyan-500/40 hover:border-cyan-400/60 rounded-xl transition-all duration-300 shadow-lg hover:shadow-cyan-500/20 w-full text-left"
          data-testid="mexc-affiliate-link"
        >
          <div className="absolute inset-0 rounded-xl bg-cyan-400/0 group-hover:bg-cyan-400/5 transition-all duration-300" />
          
          <div className="relative flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-cyan-500/20 flex items-center justify-center border border-cyan-500/30 group-hover:scale-110 transition-transform duration-300">
              <Zap size={26} className="text-cyan-400" />
            </div>
            <div className="text-left">
              <p className="font-bold text-white group-hover:text-cyan-300 transition-colors text-base">
                Trade with Advanced Tools
              </p>
              {/* Benefit Bullets - NEW */}
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Check size={10} className="text-green-400" />
                  Near-zero fees
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Check size={10} className="text-green-400" />
                  2000+ coins
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Check size={10} className="text-green-400" />
                  Pro features
                </span>
              </div>
            </div>
          </div>
          <ArrowRight size={22} className="relative text-cyan-400 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
        </button>
      </div>

      {/* Bottom Trust Indicators */}
      <div className="relative mt-4 pt-3 border-t border-gray-700/50">
        <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <Users size={12} />
            <span>100M+ users</span>
          </div>
          <div className="flex items-center gap-1">
            <Shield size={12} />
            <span>Secure & Regulated</span>
          </div>
          <div className="flex items-center gap-1">
            <TrendingUp size={12} />
            <span>24/7 Trading</span>
          </div>
        </div>
      </div>

      {/* Email Capture Section - NEW */}
      <div className="relative mt-4 pt-4 border-t border-gray-700/50">
        {!emailSubmitted ? (
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Mail size={16} className="text-blue-400" />
              <h4 className="text-sm font-semibold text-white">Save your calculation</h4>
            </div>
            <p className="text-xs text-gray-400 mb-3">
              Track this scenario and get alerts when it becomes real
            </p>
            <form onSubmit={handleEmailSubmit} className="flex gap-2 max-w-sm mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-3 py-2 text-sm bg-gray-900/60 border border-gray-700/50 rounded-lg focus:border-blue-500/50 focus:outline-none focus:ring-1 focus:ring-blue-500/30 text-white placeholder-gray-500"
                data-testid="email-capture-input"
              />
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/40 hover:border-blue-500/60 rounded-lg text-blue-400 transition-all flex items-center gap-1.5"
                data-testid="email-capture-submit"
              >
                <Bell size={14} />
                Save & Track
              </button>
            </form>
          </div>
        ) : (
          <div className="text-center p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="flex items-center justify-center gap-2">
              <Check size={16} className="text-green-400" />
              <span className="text-sm text-green-400 font-medium">Saved! We'll notify you when this scenario is achievable.</span>
            </div>
          </div>
        )}
      </div>
      
      <p className="relative text-xs text-gray-600 text-center mt-3">
        * Affiliate links. We may earn a commission at no extra cost to you.
      </p>
    </div>
  );
};

export default CryptoAffiliateBlock;
